<template>
  <div class="cash-book">
    <div class="d-flex flex-wrap align-items-center gap-3">
      <ReportChoices label="Book" icon="ti-wallet" :choices="books" :active="filters.book" @select="setBook" />
      <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />
    </div>

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter acc-select">
          <label>Account</label>
          <FormDropdown v-model="filters.account" :options="bookAccountOptions" placeholder="All accounts of the book" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Voucher, particulars, party or narration" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-flag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Opening Balance</div>
          <div class="rp-card-value">{{ balanceText(summary.opening) }}</div>
          <div class="rp-card-sub">on {{ formatDate(filters.date_from) }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-arrow-down-left"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Receipts</div>
          <div class="rp-card-value text-success">{{ formatMoney(summary.receipts) }}</div>
          <div class="rp-card-sub">money in</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-arrow-up-right"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Payments</div>
          <div class="rp-card-value text-danger">{{ formatMoney(summary.payments) }}</div>
          <div class="rp-card-sub">money out</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-wallet"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Balance</div>
          <div class="rp-card-value" :class="{ 'text-danger': Number(summary.closing) < -0.005 }">{{ balanceText(summary.closing) }}</div>
          <div class="rp-card-sub">
            {{ Number(summary.closing) < -0.005 ? 'overdrawn' : `on ${formatDate(filters.date_to)}` }}
            · {{ plural(summary.voucher_count, 'voucher') }}
          </div>
        </div>
      </div>
    </div>

    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="d-flex justify-content-between align-items-center">
          <div class="rp-panel-title mb-0">Receipts vs payments</div>
          <div class="rp-legend">
            <span><span class="rp-dot" :style="{ background: IN_COLOR }"></span>Receipts</span>
            <span><span class="rp-dot" :style="{ background: OUT_COLOR }"></span>Payments</span>
          </div>
        </div>
        <div v-show="summary.trend.length" class="rp-chart mt-2"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0 mt-2">No money moved in the period.</p>
      </div>

      <div class="rp-panel" style="grid-column: span 2">
        <div class="rp-panel-title">Balance by account</div>
        <div v-if="summary.accounts.length" class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>Account</th>
                <th class="text-end">Opening</th>
                <th class="text-end">Receipts</th>
                <th class="text-end">Payments</th>
                <th class="text-end">Closing</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in summary.accounts" :key="account.account_id">
                <td>
                  <button type="button" class="acc-link" title="Show only this account" @click="filters.account = account.account_id">
                    <i class="ti me-1 text-muted" :class="account.book === 'bank' ? 'ti-building-bank' : 'ti-cash'"></i>{{ account.account }}
                  </button>
                </td>
                <td class="text-end acc-balance">{{ balanceText(account.opening) }}</td>
                <td class="text-end text-success">{{ amountText(account.receipts) }}</td>
                <td class="text-end text-danger">{{ amountText(account.payments) }}</td>
                <td class="text-end fw-semibold acc-balance" :class="{ 'text-danger': Number(account.closing) < -0.005 }">
                  {{ balanceText(account.closing) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-muted small mb-0">No cash or bank account has a balance or postings.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold">
          {{ summary.title }}
          <span class="text-muted fw-normal small">· {{ plural(rows.length, 'entry') }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher</th>
              <th>Account</th>
              <th>Particulars</th>
              <th>Party</th>
              <th class="text-end">Receipt</th>
              <th class="text-end">Payment</th>
              <th class="text-end">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="page === 1 && !loading" class="acc-opening">
              <td>{{ formatDate(filters.date_from) }}</td>
              <td colspan="2"></td>
              <td>Opening balance</td>
              <td colspan="3"></td>
              <td class="text-end acc-balance">{{ balanceText(summary.opening) }}</td>
            </tr>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="text-nowrap">{{ formatDate(row.date) }}</td>
              <td>
                <div class="fw-medium text-nowrap">{{ row.voucher_id }}</div>
                <span class="rp-badge" :class="typeTone(row.type_key)">{{ row.type }}</span>
              </td>
              <td>{{ row.account }}</td>
              <td>
                <div>{{ row.particulars }}</div>
                <span v-if="row.narration" class="rp-sub">{{ row.narration }}</span>
              </td>
              <td>{{ row.party || '—' }}</td>
              <td class="text-end" :class="Number(row.debit) ? 'text-success' : 'text-muted'">{{ amountText(row.debit) }}</td>
              <td class="text-end" :class="Number(row.credit) ? 'text-danger' : 'text-muted'">{{ amountText(row.credit) }}</td>
              <td class="text-end fw-semibold acc-balance">{{ balanceText(row.balance) }}</td>
            </tr>
            <tr v-if="!loading && !pagedRows.length">
              <td colspan="8">
                <div class="rp-empty">
                  <i class="ti ti-wallet"></i>
                  <div class="fw-semibold">No receipts or payments</div>
                  <small class="text-muted">Try another period, book or account.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="!loading">
            <tr>
              <th colspan="5" class="text-end">Total · closing balance</th>
              <th class="text-end">{{ formatMoney(summary.receipts) }}</th>
              <th class="text-end">{{ formatMoney(summary.payments) }}</th>
              <th class="text-end acc-balance">{{ balanceText(summary.closing) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="rows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="rows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      The cash and bank heads come from the voucher setup (with every account under them); approved postings of
      {{ options.store || 'the store' }}. A transfer between two accounts of the same book shows as a receipt and a payment.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod, IN_COLOR, OUT_COLOR } from '../inventory/periodReportMixin';
import salesReport from '../sales/salesReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  book: 'cash', title: 'Cash Book', opening: 0, receipts: 0, payments: 0, closing: 0, line_count: 0, voucher_count: 0,
  accounts: [], trend: [], trend_unit: 'day',
};

export default {
  name: 'CashBookReport',
  mixins: [inventoryReport, periodReport, salesReport, accountingReport],

  reportUrl: apiUrls.cash_book_report.get,
  fileName: 'cash_book',
  columns: [],

  data() {
    return {
      IN_COLOR,
      OUT_COLOR,
      filters: { ...defaultPeriod(), book: 'cash', account: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    books() {
      return [{ key: 'cash', label: 'Cash' }, { key: 'bank', label: 'Bank' }, { key: 'all', label: 'Cash & Bank' }];
    },

    bookAccountOptions() {
      return (this.options.book_accounts || [])
        .filter((a) => this.filters.book === 'all' || a.book === this.filters.book)
        .map((a) => ({ id: a.id, text: `${a.name}${a.group ? ' · group' : ''}` }));
    },

    hasActiveFilters() {
      const { book, account, search } = this.filters;
      return this.periodChanged || book !== 'cash' || !!(account || search.trim());
    },

    visibleRows() {
      return this.rows;
    },
  },

  watch: {
    'filters.account'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { book, account } = this.filters;
      return { ...this.periodParams(), book, ...(account ? { account } : {}) };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), book: 'cash', account: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$options.fileName = { cash: 'cash_book', bank: 'bank_book' }[this.filters.book] || 'cash_bank_book';
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Receipts', field: 'receipts', color: IN_COLOR },
        { label: 'Payments', field: 'payments', color: OUT_COLOR },
      ]));
    },

    setBook(choice) {
      if (choice.key === this.filters.book) return;
      this.applyingFilters = true;
      Object.assign(this.filters, { book: choice.key, account: null });
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.onFilterChange();
      });
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      const account = this.filters.account
        ? this.optionName(this.options.book_accounts || [], this.filters.account, '—') : 'All accounts of the book';
      this.printStatement(
        s.title,
        [this.periodText(), `Account: ${account}`, this.storeLabel(), search && `Search: ${search}`],
        [['Opening', this.balanceText(s.opening)], ['Receipts', this.formatMoney(s.receipts)],
          ['Payments', this.formatMoney(s.payments)], ['Closing', this.balanceText(s.closing)]],
        [
          { label: 'Date', value: (r) => (r.date ? this.formatDate(r.date) : '') },
          { label: 'Voucher', value: (r) => r.voucher_id || '' },
          { label: 'Type', value: (r) => r.type || '' },
          { label: 'Account', value: (r) => r.account || '' },
          { label: 'Particulars', value: (r) => r.particulars },
          { label: 'Party', value: (r) => r.party || '' },
          { label: 'Narration', value: (r) => r.narration || '' },
          { label: 'Receipt', value: (r) => (r.debit === undefined ? '' : this.amountText(r.debit)), num: true },
          { label: 'Payment', value: (r) => (r.credit === undefined ? '' : this.amountText(r.credit)), num: true },
          { label: 'Balance', value: (r) => this.balanceText(r.balance), num: true },
        ],
        [{ particulars: 'Opening balance', balance: s.opening, _bold: true }, ...this.rows],
        { Particulars: 'Closing balance', Receipt: this.formatMoney(s.receipts), Payment: this.formatMoney(s.payments), Balance: this.balanceText(s.closing) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
